import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  onConfirmDangerMessage,
  onErrorMessage,
  onSuccessMessage,
  trackEvent,
  useParamsState,
  useValidationErrors,
} from '@/helpers'
import {
  useCalculateMargin,
  useCreateQuotation,
  useDollarQuotationLatest,
  useQuotationDetail,
  useUpdateQuotation,
} from '@/services'

import { Button, Text } from '@mercai/clever'

import { useAuth } from '@/providers'
import { ROUTES_ADMIN } from '@/router'
import {
  AddressProps,
  CalculateMarginPayloadProps,
  ProfileProps,
  QuotationProps,
} from '@/types'
import {
  AddressDrawerForm,
  BuyerDrawerForm,
  CatalogProducts,
  DrawerBuyerProfile,
  GeneralInformation,
  LogisticData,
  OrderData,
  QuotationDialog,
  QuotationMetrics,
  QuotationStatusSelect,
} from '../../components'
import { getQuotationMetricsPayload } from '../../helpers'
import {
  checkIsCatalogProductsPrices,
  checkIsCompleteGeneralInformation,
  checkIsCompleteLogisticData,
  checkIsCompleteOrderData,
  schemaQuotation,
} from '../../quotation-validation'
import {
  accordionsByStatusObject,
  getQuotationWithOrdenedProducts,
} from './form.constants'

import styles from './form.module.css'

export const QuotationV2Form = () => {
  const { t } = useTranslation('screenBusinessV2')

  const { uuid } = useParams()

  const isNewRegister = !uuid

  const navigate = useNavigate()
  const location = useLocation()
  const page = location.pathname.split('/').pop()

  const { mutateAsync: mutateAsyncUpdateQuotation } = useUpdateQuotation(uuid)
  const { mutateAsync: mutateAsyncCreateQuotation } = useCreateQuotation()

  const { data: dataDollarQuotationLatest } = useDollarQuotationLatest()

  const { whoAmI } = useAuth()

  const [quotationValues, setQuotationValues] = useState<
    Partial<QuotationProps>
  >({
    disableNotifications: false,
    includeIva: true,
  } as Partial<QuotationProps>)

  const { data: quotation, isLoading: isLoadingQuotation } =
    useQuotationDetail(uuid)

  const [quotationOrdened, setQuotationOrdened] = useState<QuotationProps>()

  const setBuyerProfile = (buyerProfile: ProfileProps) => {
    setQuotationOrdened({
      ...quotationOrdened,
      buyerProfile,
      buyerProfileUuid: buyerProfile.uuid,
    } as QuotationProps)
  }

  const setAddress = (address: Partial<AddressProps>) => {
    setQuotationOrdened({
      ...quotationOrdened,
      address,
      addressUuid: address.uuid,
      deliveryAddress: address,
      deliveryAddressUuid: address.uuid,
    } as QuotationProps)
  }

  const isCompleteGeneralInformation = checkIsCompleteGeneralInformation({
    address: quotationOrdened?.deliveryAddress,
    buyerProfile: quotationOrdened?.buyerProfile,
    paymentFormOption: quotationValues?.paymentFormOption,
    financingCost: quotationValues?.financingCost,
    creditTerm: quotationValues?.creditTerm,
  })

  const isCompleteOrderData = checkIsCompleteOrderData({
    expiresAt: quotationValues?.expiresAt,
    expectedDeliveryDays: quotationValues?.expectedDeliveryDays,
    totalFreightPrice: quotationValues?.totalFreightPrice,
    purchaseOnCredit: quotationValues?.purchaseOnCredit,
    financingCost: quotationValues?.financingCost,
  })

  const isCompleteLogisticData = checkIsCompleteLogisticData({
    firstMileFreightCost: quotationValues?.firstMileFreightCost,
    lastMileFreightCost: quotationValues?.lastMileFreightCost,
    customsFreightCost: quotationValues?.customsFreightCost,
  })

  const isCompleteCatalogProductsPrices = checkIsCatalogProductsPrices(
    quotationValues?.products,
  )

  const [quotationDialogIsOpen, setQuotationDialogIsOpen] = useState(false)

  const [dataForMetrics, setDataForMetrics] =
    useState<CalculateMarginPayloadProps>()

  const { data: dataCalculateMargin, mutateAsync: mutateAsyncCalculateMargin } =
    useCalculateMargin()

  const incomplete = useMemo(
    () => !dataCalculateMargin?.success,
    [dataCalculateMargin],
  )

  const [queryParams, setQueryParams] = useParamsState({
    form: '',
  })

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaQuotation(t),
    quotationValues,
  )

  const onCloseDrawerForm = () => {
    setQueryParams({ ...queryParams, form: '' })
  }

  const getMetrics = (quotation: QuotationProps | undefined = undefined) => {
    const data = quotation || quotationValues
    if (data) {
      setDataForMetrics(
        getQuotationMetricsPayload({
          quotationValues: data as QuotationProps,
        }),
      )
    }
  }

  const accordionsComponents = {
    'general-information': (position: number) => (
      <GeneralInformation
        position={position}
        key="general-information"
        isComplete={isCompleteGeneralInformation}
        isLoading={isLoadingQuotation}
        buyerProfile={quotationOrdened?.buyerProfile}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        address={quotationOrdened?.deliveryAddress}
        onHandleBuyerDrawerOpen={() =>
          setQueryParams({ ...queryParams, form: 'buyer' })
        }
        onHandleAddressDrawerOpen={() =>
          setQueryParams({ ...queryParams, form: 'address' })
        }
        isEditable={true}
        getMetrics={getMetrics}
        errors={errors}
      />
    ),
    'order-data': (position: number) => (
      <OrderData
        position={position}
        key="order-data"
        isComplete={isCompleteOrderData}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        errors={errors}
        getMetrics={getMetrics}
      />
    ),
    'logistic-data': (position: number) => (
      <LogisticData
        position={position}
        key="logistic-data"
        isComplete={isCompleteLogisticData}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        errors={errors}
        getMetrics={getMetrics}
      />
    ),
    'catalog-products': (position: number) => (
      <CatalogProducts
        position={position}
        key="catalog-products"
        validation={isCompleteCatalogProductsPrices}
        isEditable={true}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        catalogProducts={quotationValues?.products || []}
        onHandleCatalogProductsDrawerOpen={() => { }}
        getMetrics={getMetrics}
        errors={errors}
        dollarQuotation={dataDollarQuotationLatest?.data?.value}
        grossMargin={dataCalculateMargin?.data?.grossMargin}
        cm3Margin={dataCalculateMargin?.data?.cm3Margin}
      />
    ),
  }

  const fixTo2Decimals = (value?: number | string) => {
    if (!value) return 0

    return +Number(value)?.toFixed?.(2) || 0
  }

  const onHandleSubmitComplete = async () => {
    if (!isValid) {
      return onHandleListenErrors(true)
    }

    onHandleListenErrors(false)

    const confirm = await onConfirmDangerMessage(
      t('quotationCreateConfirmTitle'),
      isNewRegister
        ? t('quotationCreateConfirmMessage')
        : t('quotationUpdateConfirmMessage'),
      t('quotationCreateConfirmButtonCreate'),
      t('quotationCreateConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    let response = {
      success: true,
      message: '',
    }

    if (isNewRegister) {
      response = await mutateAsyncCreateQuotation({
        ...quotationValues,
        customsFreightCost: fixTo2Decimals(quotationValues?.customsFreightCost),
        status: 'pending',
        transportCompany: undefined,
        paymentFormOption: quotationValues?.paymentFormOption
          ? quotationValues?.paymentFormOption
          : undefined,
      } as QuotationProps)
    } else {
      response = await mutateAsyncUpdateQuotation({
        ...quotationValues,
        customsFreightCost: fixTo2Decimals(quotationValues?.customsFreightCost),
        transportCompany: undefined,
        paymentFormOption: quotationValues?.paymentFormOption
          ? quotationValues?.paymentFormOption
          : undefined,
      } as QuotationProps)
    }

    if (response.success) {
      onSuccessMessage(
        t('quotationFormSuccessCreateTitle'),
        isNewRegister
          ? t('quotationFormSuccessCreateMessage')
          : t('quotationFormSuccessUpdateMessage'),
      )

      window.location.href = `${ROUTES_ADMIN.BUSINESS.QUOTATION.SHOW.fullPath(uuid || '')}`
    } else {
      onErrorMessage(t('quotationFormErrorCreateTitle'), response?.message)
    }
  }

  const onHandleSubmit = () => {
    if (
      quotationValues?.products &&
      quotationValues?.products?.filter((product) => product.catalogProductUuid)
        ?.length < quotationValues?.products?.length
    ) {
      return setQuotationDialogIsOpen(true)
    }

    onHandleSubmitComplete()
  }

  const onCancelEdit = async () => {
    const confirm = await onConfirmDangerMessage(
      t('quotationCreateConfirmTitle'),
      t('quotationCreateConfirmCancelMessage'),
      t('quotationCreateConfirmButtonCreate'),
      t('quotationCreateConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    return navigate(
      isNewRegister
        ? ROUTES_ADMIN.BUSINESS.QUOTATION.fullPath()
        : ROUTES_ADMIN.BUSINESS.QUOTATION.SHOW.fullPath(uuid || ''),
    )
  }

  useEffect(() => {
    setQuotationValues({
      ...quotationOrdened,
      ...quotationValues,
      customsFreightCost: fixTo2Decimals(quotationOrdened?.customsFreightCost),
      discount: quotationOrdened?.discount || '0.00',
      logisticTransportCompanyUuid:
        quotationOrdened?.logisticTransportCompany?.uuid,
      status: quotationOrdened?.status || 'pending',
      dollarExchangeRate: dataDollarQuotationLatest?.data?.value,
      buyerProfileUuid: quotationOrdened?.buyerProfile?.uuid,
      buyerProfile: quotationOrdened?.buyerProfile,
      deliveryAddressUuid: quotationOrdened?.deliveryAddress?.uuid,
      deliveryAddress: quotationOrdened?.deliveryAddress,
      disableNotifications: quotationOrdened?.disableNotifications,
      paymentFormOption: quotationOrdened?.paymentFormOption || 'upfront',
      paymentMethodOption:
        quotationOrdened?.paymentMethodOption || 'bank_transfer',
      products:
        quotationOrdened?.products?.map((product, i) => ({
          ...product,
          ...quotationValues?.products?.[i],
          catalogProductUuid: product.catalogProduct?.uuid,
          supplierUuid: product.supplier?.uuid,
          supplierProductUuid:
            product.supplierProduct?.uuid || product?.supplierProductUuid || '',
        })) || [],
    })
  }, [dataDollarQuotationLatest, whoAmI, quotationOrdened])

  useEffect(() => {
    getMetrics(quotation?.data)

    setQuotationOrdened(getQuotationWithOrdenedProducts(quotation?.data))
  }, [quotation])

  useEffect(() => {
    trackEvent('adminQuotationUpdatePageView', { page })
  }, [])

  useEffect(() => {
    if (
      dataForMetrics &&
      dataForMetrics?.customsFreightCost &&
      Object.keys(dataForMetrics).length > 0
    )
      mutateAsyncCalculateMargin(dataForMetrics)
  }, [dataForMetrics])

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Text variant="text-small" className={styles.breadcrumb}>
          <Link to={''} onClick={onCancelEdit}>{`${t('quotationTitle')}`}</Link>
          {` / `}
          <span>
            {isNewRegister
              ? t('quotationTitleCreate')
              : quotationOrdened?.reference}
          </span>
        </Text>

        <div className={styles['header-content']}>
          {!isNewRegister ? (
            <div>
              <div className={styles['sub-title']}>
                <Text variant="title-large">
                  {t(
                    quotationOrdened?.products?.length === 1
                      ? 'quotationIdentifier'
                      : 'quotationIdentifierPlural',
                    {
                      rfq: quotationOrdened?.reference,
                      quantity: quotationOrdened?.products?.length,
                    },
                  )}
                </Text>
                <QuotationStatusSelect quotation={quotationOrdened} />
              </div>
              <Text
                variant="text-base"
                className={styles['sub-title-instructions']}
              >
                {t('instructionGenerateProposals')}
              </Text>
            </div>
          ) : (
            <div>
              <div className={styles['sub-title']}>
                <Text variant="title-large">
                  {t('instructionCreateQuotationTitle')}
                </Text>
              </div>
              <Text
                variant="text-base"
                className={styles['sub-title-instructions']}
              >
                {t('instructionCreateQuotationDescription')}
              </Text>
            </div>
          )}
          <div className={styles['header-actions']}>
            <Button variant="secondary" onClick={onCancelEdit}>
              {t('buttonCancelEditQuotation')}
            </Button>
            <Button
              variant="primary"
              onClick={onHandleSubmit}
              disabled={
                quotationOrdened?.status !== 'pending' &&
                quotationOrdened?.status !== 'created' &&
                !isNewRegister
              }
            >
              {isNewRegister
                ? t('buttonCreateQuotation')
                : t('buttonEditQuotation')}
            </Button>
          </div>
        </div>
      </header>

      <QuotationMetrics
        values={dataCalculateMargin?.data}
        incomplete={incomplete}
      />

      {accordionsByStatusObject[quotationOrdened?.status || 'pending'].map(
        (key, i) =>
          accordionsComponents[key as keyof typeof accordionsComponents](i + 1),
      )}

      {!isNewRegister && (
        <BuyerDrawerForm
          uuid={quotationOrdened?.buyerProfile?.uuid || ''}
          isOpen={queryParams.form === 'buyer'}
          onHandleOpen={onCloseDrawerForm}
          setBuyerProfile={setBuyerProfile}
          onSubmit={() => setQueryParams({ ...queryParams, form: 'address' })}
        />
      )}

      {isNewRegister && (
        <DrawerBuyerProfile
          isOpen={queryParams.form === 'buyer'}
          onHandleOpen={onCloseDrawerForm}
          setBuyerProfile={(buyer) => setBuyerProfile(buyer as ProfileProps)}
          buyerProfile={quotationOrdened?.buyerProfile}
          onSubmit={() => setQueryParams({ ...queryParams, form: 'address' })}
        />
      )}

      <AddressDrawerForm
        quotation={quotationOrdened as QuotationProps}
        isOpen={queryParams.form === 'address'}
        onHandleOpen={onCloseDrawerForm}
        setAddress={setAddress}
        onBack={() => setQueryParams({ ...queryParams, form: 'buyer' })}
        isCreate={isNewRegister}
      />

      <QuotationDialog
        onHandleSubmit={onHandleSubmitComplete}
        isOpen={quotationDialogIsOpen}
        onHandleClose={() => setQuotationDialogIsOpen(false)}
      />
    </div>
  )
}
